import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ReassignSeatsDialog, SyncPreviewDialog } from './components';
import { getSeatsSyncStatus } from './services';
import {
	PunchupColumnHeader,
	PunchupDataRow,
	PunchupDateTimeCell,
	PunchupHeaderRow,
	PunchupTableContainer,
	PunchupTableEmptyRow,
	PunchupTableLoadingRow,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupSearch } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupSearch';
import { PunchupTablePage } from 'features/common/components/Punchup/pages/PunchupTablePage';
import { PunchupTablePageHeader } from 'features/common/components/Punchup/composites/tables/PunchupTablePageHeader';
import { PunchupTag } from 'features/common/components/Punchup/primitives/PunchupTag/PunchupTag';
import { convertUTCToDateBarFormat } from 'utils/utils';
import { SeatsSyncEvent } from './types';
import styles from './SeatsSync.module.css';

// Chip colors mirror --punchup-color-danger / --punchup-color-warning
// (PunchupTag takes a hex, same pattern as CompedTickets statusColors.ts).
const DANGER = '#ef4444';
const WARNING = '#f59e0b';

interface DriftChip {
	key: string;
	label: string;
	background: string;
}

interface DriftSummary {
	chips: DriftChip[];
	details: string[];
}

export const SeatsSync = (): JSX.Element => {
	const { t } = useTranslation();
	const [searchQuery, setSearchQuery] = useState('');
	const [previewEvent, setPreviewEvent] = useState<SeatsSyncEvent | null>(null);
	const [reassignEvent, setReassignEvent] = useState<SeatsSyncEvent | null>(null);

	const { data, isLoading, isFetching, isError, refetch } = useQuery({
		queryKey: ['seats-sync-status'],
		queryFn: getSeatsSyncStatus,
	});

	// Severity chips summarize each drift class at a glance; the detail lines
	// under them carry the exact numbers. Danger = tickets can double-sell or
	// silently stop selling; warning = capacity/coverage drift. A check failure
	// replaces everything — the drift is unknown, not absent.
	const driftSummary = (event: SeatsSyncEvent): DriftSummary => {
		if (event.error) {
			return {
				chips: [{ key: 'error', label: t('seatsSync-chip-error', 'check failed'), background: DANGER }],
				details: [event.error],
			};
		}
		const chips: DriftChip[] = [];
		const details: string[] = [];

		const drifted = (event.ticket_types ?? []).filter((ticketType) => !ticketType.in_sync);
		const offChart = drifted.filter(
			(ticketType) => ticketType.chart_capacity === null || ticketType.expected_total_amount === null
		);
		const capacity = drifted.filter(
			(ticketType) =>
				ticketType.expected_total_amount !== null && ticketType.total_amount !== ticketType.expected_total_amount
		);
		const unbooked = drifted.filter(
			(ticketType) => ticketType.chart_booked !== null && ticketType.sold > ticketType.chart_booked
		);
		const unbookedCount = unbooked.reduce(
			(sum, ticketType) => sum + (ticketType.sold - (ticketType.chart_booked ?? 0)),
			0
		);
		// More tickets sold than physical seats exist in the category — the
		// hard oversell. Distinct from the unbooked deficit: that one also
		// covers lost bookings, this one means the room cannot hold the sales.
		const oversold = drifted.filter(
			(ticketType) => ticketType.chart_capacity !== null && ticketType.sold > ticketType.chart_capacity
		);
		const oversoldCount = oversold.reduce(
			(sum, ticketType) => sum + (ticketType.sold - (ticketType.chart_capacity ?? 0)),
			0
		);

		if (oversoldCount > 0) {
			chips.push({
				key: 'oversold',
				label: `${t('seatsSync-chip-oversold', 'OVERSOLD by')} ${oversoldCount}`,
				background: DANGER,
			});
			for (const ticketType of oversold) {
				details.push(
					`${ticketType.name} — ${ticketType.sold} ${t('seatsSync-sold', 'sold')}, ${ticketType.chart_capacity} ${t(
						'seatsSync-seatsOnChart',
						'seats on chart'
					)}`
				);
			}
		}
		if (unbookedCount > 0) {
			chips.push({
				key: 'unbooked',
				label: `${unbookedCount} ${t('seatsSync-chip-unbooked', 'sold w/o seat')}`,
				background: DANGER,
			});
			for (const ticketType of unbooked) {
				details.push(
					`${ticketType.name} — ${ticketType.sold} ${t('seatsSync-sold', 'sold')}, ${ticketType.chart_booked} ${t(
						'seatsSync-booked',
						'booked on chart'
					)}`
				);
			}
		}
		if (event.orphaned_bookings?.length) {
			chips.push({
				key: 'orphanedBookings',
				label: `${event.orphaned_bookings.length} ${t('seatsSync-chip-orphanedBookings', 'booked, no ticket')}`,
				background: DANGER,
			});
			const shown = event.orphaned_bookings
				.slice(0, 6)
				.map((booking) => `${booking.seat_label} (${booking.category_label || `#${booking.seats_category_key}`})`)
				.join(', ');
			const extra = event.orphaned_bookings.length - 6;
			details.push(
				`${t('seatsSync-orphanedBookings', 'Booked with no ticket (failed release)')}: ${shown}${
					extra > 0 ? ` +${extra}` : ''
				}`
			);
		}
		if (event.duplicate_keys?.length) {
			chips.push({
				key: 'duplicates',
				label:
					event.duplicate_keys.length === 1
						? t('seatsSync-chip-duplicate', 'duplicate category')
						: `${event.duplicate_keys.length} ${t('seatsSync-chip-duplicates', 'duplicate categories')}`,
				background: DANGER,
			});
			for (const duplicate of event.duplicate_keys) {
				const label = duplicate.category_label || `#${duplicate.seats_category_key}`;
				details.push(`${label}: ${duplicate.ticket_types.map((claimant) => claimant.name).join(' + ')}`);
			}
		}
		if (offChart.length > 0) {
			chips.push({
				key: 'offChart',
				label: `${offChart.length} ${t('seatsSync-chip-offChart', 'off-chart')}`,
				background: DANGER,
			});
			for (const ticketType of offChart) {
				details.push(`${ticketType.name} — ${t('seatsSync-categoryMissing', 'category gone from the chart')}`);
			}
		}
		if (capacity.length > 0) {
			chips.push({
				key: 'capacity',
				label: `${capacity.length} ${t('seatsSync-chip-capacity', 'capacity')}`,
				background: WARNING,
			});
			for (const ticketType of capacity) {
				details.push(
					`${ticketType.name} — ${ticketType.total_amount} → ${ticketType.expected_total_amount} (${t(
						'seatsSync-chart',
						'chart'
					)} ${ticketType.chart_capacity}, ${t('seatsSync-sold', 'sold')} ${ticketType.sold})`
				);
			}
		}
		if (event.unmapped_categories?.length) {
			chips.push({
				key: 'unmapped',
				label: `${event.unmapped_categories.length} ${t('seatsSync-chip-unmapped', 'unmapped')}`,
				background: WARNING,
			});
			details.push(`${t('seatsSync-unmapped', 'No ticket type for')}: ${event.unmapped_categories.join(', ')}`);
		}
		return { chips, details };
	};

	const events = data?.events ?? [];
	const visibleEvents = useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		const filtered = !query
			? events
			: events.filter(
					(event) =>
						event.partner_name?.toLowerCase().includes(query) ||
						event.event_name?.toLowerCase().includes(query) ||
						String(event.partner_id).includes(query)
			  );
		// Soonest show first — those are the ones that sell (and break) next.
		return [...filtered].sort((a, b) => {
			if (!a.start_date) return 1;
			if (!b.start_date) return -1;
			return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
		});
	}, [events, searchQuery]);

	const title = t('seatsSync-title', 'Seats Sync');

	return (
		<div className={styles.pageSurface}>
			<PunchupTablePage
				pageName={title}
				header={
					<PunchupTablePageHeader
						title={title}
						description={t(
							'seatsSync-description',
							'Live and upcoming seated events whose ticket counts no longer match their seating chart. Review each event to see and apply the repair.'
						)}
						onRefresh={() => refetch()}
						refreshLabel={t('seatsSync-refresh', 'Refresh')}
						isRefreshing={isFetching && !isLoading}
					/>
				}
				search={
					<PunchupSearch
						value={searchQuery}
						onChange={(changeEvent) => setSearchQuery(changeEvent.target.value)}
						onClear={() => setSearchQuery('')}
						placeholder={t('seatsSync-search', 'Filter by partner or event')}
						width={300}
					/>
				}
				overlays={
					<>
						{previewEvent && (
							<SyncPreviewDialog
								event={previewEvent}
								onClose={() => setPreviewEvent(null)}
								onReassign={() => {
									setReassignEvent(previewEvent);
									setPreviewEvent(null);
								}}
							/>
						)}
						{reassignEvent && <ReassignSeatsDialog event={reassignEvent} onClose={() => setReassignEvent(null)} />}
					</>
				}
			>
				<PunchupTableContainer columns="max-content minmax(160px, max-content) max-content minmax(280px, 1fr) max-content">
					<thead>
						<PunchupHeaderRow>
							<PunchupColumnHeader>{t('seatsSync-column-date', 'Date')}</PunchupColumnHeader>
							<PunchupColumnHeader>{t('seatsSync-column-event', 'Event')}</PunchupColumnHeader>
							<PunchupColumnHeader>{t('seatsSync-column-partner', 'Partner')}</PunchupColumnHeader>
							<PunchupColumnHeader>{t('seatsSync-column-drift', 'Out of sync')}</PunchupColumnHeader>
							<PunchupColumnHeader>{t('seatsSync-column-action', 'Action')}</PunchupColumnHeader>
						</PunchupHeaderRow>
					</thead>
					<tbody>
						{isLoading && <PunchupTableLoadingRow />}
						{!isLoading && isError && (
							<PunchupTableEmptyRow
								text={t(
									'seatsSync-loadError',
									"Couldn't load the drift report — the API refused the request. Your login needs super-admin access on the ticketing API."
								)}
							/>
						)}
						{!isLoading && !isError && visibleEvents.length === 0 && (
							<PunchupTableEmptyRow
								text={
									searchQuery
										? t('seatsSync-emptyFiltered', 'No drifted events match this filter.')
										: t('seatsSync-empty', 'All live and upcoming seated events match their charts.')
								}
							/>
						)}
						{visibleEvents.map((event) => {
							const drift = driftSummary(event);
							return (
								<PunchupDataRow key={event.event_id}>
									<PunchupDateTimeCell>
										{event.start_date ? (
											<>
												<div className={styles.entityName}>
													{convertUTCToDateBarFormat(event.start_date, event.time_zone).date}
												</div>
												<div className={styles.entityId}>
													{convertUTCToDateBarFormat(event.start_date, event.time_zone).time}
												</div>
											</>
										) : (
											'—'
										)}
									</PunchupDateTimeCell>
									<td>
										<div className={styles.entityName}>{event.event_name}</div>
										<div className={styles.entityId}>#{event.event_id}</div>
									</td>
									<td>
										<div className={styles.entityName}>{event.partner_name || '—'}</div>
										<div className={styles.entityId}>#{event.partner_id}</div>
									</td>
									<td>
										<div className={styles.driftCell}>
											<div className={styles.driftChips}>
												{drift.chips.map((chip) => (
													<PunchupTag key={chip.key} label={chip.label} background={chip.background} size="sm" />
												))}
											</div>
											<div className={styles.driftDetails}>
												{drift.details.map((line, index) => (
													<div key={`${event.event_id}-${index}`}>{line}</div>
												))}
											</div>
										</div>
									</td>
									<td>
										<div className={styles.actions}>
											<PunchupButton variant="secondary" onClick={() => setPreviewEvent(event)}>
												{t('seatsSync-reviewButton', 'Review')}
											</PunchupButton>
											<PunchupButton variant="primary" onClick={() => setReassignEvent(event)}>
												{t('seatsSync-reassignButton', 'Reassign')}
											</PunchupButton>
										</div>
									</td>
								</PunchupDataRow>
							);
						})}
					</tbody>
				</PunchupTableContainer>
			</PunchupTablePage>
		</div>
	);
};
